import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService, Appointment } from '../../services/apiService';

interface DashboardStats {
  todaysAppointments: number;
  completedAppointments: number;
  pendingConfirmations: number;
  noShows: number;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    todaysAppointments: 0,
    completedAppointments: 0,
    pendingConfirmations: 0,
    noShows: 0
  });
  const [upcomingAppointments, setUpcomingAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load upcoming appointments
      const appointmentsResponse = await apiService.getUpcomingAppointments();
      setUpcomingAppointments(appointmentsResponse.data.data || []);

      // Calculate stats from appointments
      const today = new Date().toISOString().split('T')[0];
      const todaysAppointments = appointmentsResponse.data.data?.filter(
        (apt: Appointment) => apt.appointment_date === today
      ).length || 0;

      const completedAppointments = appointmentsResponse.data.data?.filter(
        (apt: Appointment) => apt.status === 'completed'
      ).length || 0;

      const pendingConfirmations = appointmentsResponse.data.data?.filter(
        (apt: Appointment) => apt.status === 'scheduled'
      ).length || 0;

      const noShows = appointmentsResponse.data.data?.filter(
        (apt: Appointment) => apt.status === 'no_show'
      ).length || 0;

      setStats({
        todaysAppointments,
        completedAppointments,
        pendingConfirmations,
        noShows
      });
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'no_show':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'Confirmed';
      case 'pending':
        return 'Pending';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      case 'no_show':
        return 'No Show';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-admin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-600">Welcome to SeaDent Admin Dashboard</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="text-2xl">📅</div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Today's Appointments</p>
              <p className="text-2xl font-bold text-gray-900">{stats.todaysAppointments}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="text-2xl">✅</div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{stats.completedAppointments}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="text-2xl">⏳</div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending Confirmations</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingConfirmations}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="text-2xl">❌</div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">No Shows</p>
              <p className="text-2xl font-bold text-gray-900">{stats.noShows}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Appointments */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Upcoming Appointments</h2>
          <p className="text-sm text-gray-600">Next 7 days</p>
        </div>
        <div className="divide-y divide-gray-200">
          {upcomingAppointments.length === 0 ? (
            <div className="px-6 py-4 text-center text-gray-500">
              No upcoming appointments
            </div>
          ) : (
            upcomingAppointments.slice(0, 10).map((appointment) => (
              <div key={appointment.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {appointment.patient_name.charAt(0)}
                          </span>
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">{appointment.patient_name}</p>
                        <p className="text-sm text-gray-500">
                          {formatDate(appointment.appointment_date)} at {formatTime(appointment.start_time)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="text-sm text-gray-500">
                      <p>{appointment.provider_name}</p>
                      <p>{appointment.procedure_name}</p>
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(appointment.status)}`}>
                      {getStatusLabel(appointment.status)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/admin/calendar"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <div className="text-2xl mr-3">📅</div>
            <div>
              <p className="text-sm font-medium text-gray-900">Calendar</p>
              <p className="text-xs text-gray-500">View and manage appointments</p>
            </div>
          </Link>
          
          <Link
            to="/admin/appointments"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <div className="text-2xl mr-3">📋</div>
            <div>
              <p className="text-sm font-medium text-gray-900">Appointments</p>
              <p className="text-xs text-gray-500">Manage appointment list</p>
            </div>
          </Link>
          
          <Link
            to="/admin/providers"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <div className="text-2xl mr-3">👨‍⚕️</div>
            <div>
              <p className="text-sm font-medium text-gray-900">Providers</p>
              <p className="text-xs text-gray-500">Manage doctors and staff</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 